import React, { useState, useEffect, useCallback } from 'react'
import { Card, Row, Col, Table, Tag, Select, Switch, Statistic, message, Button, Tooltip } from 'antd'
import {
  AlertOutlined,
  BugOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useAtom } from 'jotai'
import axios from 'axios'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
} from 'recharts'
import type { ErrorLog } from '@shared/common-types.js'
import { themeAtom } from '../atoms.js'
import { getChartTheme } from './charts/chartTheme.js'

type ErrorStats = {
  overview: {
    totalErrors: number
    errors24h: number
    errors7d: number
    errors30d: number
    criticalUnresolved: number
  }
  statistics: {
    bySeverity: Array<{ severity: string; count: number }>
    byType: Array<{ type: string; count: number }>
    bySource: Array<{ source: string; count: number }>
  }
}

type ErrorTrends = {
  daily: Array<{ date: string; count: number; severity: string }>
  hourly: Array<{ hour: string; count: number }>
}

export default function ErrorDashboard() {
  const [theme] = useAtom(themeAtom)
  const [stats, setStats] = useState<ErrorStats | null>(null)
  const [trends, setTrends] = useState<ErrorTrends | null>(null)
  const [errors, setErrors] = useState<ErrorLog[]>([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  })
  const [filters, setFilters] = useState({
    severity: undefined as string | undefined,
    type: undefined as string | undefined,
    resolved: undefined as boolean | undefined,
  })

  const t = getChartTheme(theme)

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/error/stats')
      setStats(response.data.data)
    } catch (error) {
      console.error('获取错误统计失败:', error)
      message.error('获取错误统计失败')
    }
  }

  const fetchTrends = async () => {
    try {
      const response = await axios.get('/api/error/trends')
      setTrends(response.data.data)
    } catch (error) {
      console.error('获取错误趋势失败:', error)
      message.error('获取错误趋势失败')
    }
  }

  const fetchErrors = useCallback(
    async (page = 1) => {
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          pageSize: pagination.pageSize.toString(),
        })

        if (filters.severity) params.append('severity', filters.severity)
        if (filters.type) params.append('type', filters.type)
        if (filters.resolved !== undefined) params.append('resolved', filters.resolved.toString())

        const response = await axios.get(`/api/error/list?${params}`)
        const data = response.data.data
        setErrors(data.list)
        setPagination({
          current: data.pagination.current,
          pageSize: data.pagination.pageSize,
          total: data.pagination.total,
          totalPages: data.pagination.totalPages,
        })
      } catch (error) {
        console.error('获取错误列表失败:', error)
        message.error('获取错误列表失败')
      }
    },
    [pagination.pageSize, filters.severity, filters.type, filters.resolved],
  )

  const resolveError = async (errorId: number) => {
    try {
      await axios.patch(`/api/error/${errorId}/resolve`, {
        resolvedBy: 'admin',
      })
      message.success('错误已标记为已解决')
      fetchErrors(pagination.current)
      fetchStats()
    } catch (error) {
      console.error('标记错误解决失败:', error)
      message.error('操作失败')
    }
  }

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchStats(), fetchTrends(), fetchErrors()])
      setLoading(false)
    }
    loadData()
  }, [fetchErrors])

  useEffect(() => {
    fetchErrors(1)
  }, [fetchErrors, filters])

  const tooltipStyle = t.tooltipContentStyle

  const getSeverityColor = (severity: string) => {
    const colors = {
      low: 'green',
      medium: 'orange',
      high: 'red',
      critical: 'magenta',
    }
    return colors[severity as keyof typeof colors] || 'default'
  }

  const getSeverityIcon = (severity: string) => {
    const icons = {
      low: <CheckCircleOutlined />,
      medium: <WarningOutlined />,
      high: <ExclamationCircleOutlined />,
      critical: <AlertOutlined />,
    }
    return icons[severity as keyof typeof icons] || <BugOutlined />
  }

  const columns = [
    {
      title: '时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '严重程度',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity: string) => (
        <Tag color={getSeverityColor(severity)} icon={getSeverityIcon(severity)}>
          {severity.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => (
        <Tag color="blue">
          {type === 'security_attack'
            ? '安全攻击'
            : type === 'server_error'
              ? '服务器错误'
              : type === 'client_error'
                ? '客户端错误'
                : type}
        </Tag>
      ),
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 140,
      render: (source: string) => source || '未知',
    },
    {
      title: '错误信息',
      dataIndex: 'message',
      key: 'message',
      ellipsis: { showTitle: false },
      render: (message: string) => (
        <Tooltip title={message} placement="topLeft">
          {message || '无详细信息'}
        </Tooltip>
      ),
    },
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'resolved',
      key: 'resolved',
      width: 100,
      render: (resolved: boolean, record: ErrorLog) =>
        resolved ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            已解决
          </Tag>
        ) : (
          <Button type="link" size="small" onClick={() => resolveError(record.id)}>
            标记解决
          </Button>
        ),
    },
  ]

  return (
    <div className="space-y-6 p-6">
      {/* 统计概览 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="未解决的严重错误"
              value={stats?.overview.criticalUnresolved || 0}
              prefix={<AlertOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="总错误数" value={stats?.overview.totalErrors || 0} prefix={<BugOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="24小时错误"
              value={stats?.overview.errors24h || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="7天错误"
              value={stats?.overview.errors7d || 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#d46b08' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 趋势图表 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="错误趋势">
            {trends && trends.daily.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trends.daily} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={t.gridColor} />
                  <XAxis dataKey="date" tick={t.axisTickProps} axisLine={t.axisLineProps} tickLine={t.tickLineProps} />
                  <YAxis tick={t.axisTickProps} axisLine={t.axisLineProps} tickLine={t.tickLineProps} />
                  <RechartsTooltip
                    contentStyle={tooltipStyle}
                    labelStyle={t.tooltipLabelStyle}
                    itemStyle={t.tooltipItemStyle}
                    cursor={{ stroke: t.cursorColor }}
                  />
                  <Line type="monotone" dataKey="count" stroke="#3b82f6" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[300px] items-center justify-center text-gray-400">
                {trends ? '数据为空' : 'loading...'}
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="24小时分布">
            {trends && trends.hourly.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={trends.hourly} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={t.gridColor} />
                  <XAxis dataKey="hour" tick={t.axisTickProps} axisLine={t.axisLineProps} tickLine={t.tickLineProps} />
                  <YAxis tick={t.axisTickProps} axisLine={t.axisLineProps} tickLine={t.tickLineProps} />
                  <RechartsTooltip
                    contentStyle={tooltipStyle}
                    labelStyle={t.tooltipLabelStyle}
                    itemStyle={t.tooltipItemStyle}
                    cursor={{ fill: t.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}
                  />
                  <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[300px] items-center justify-center text-gray-400">
                {trends ? '数据为空' : 'loading...'}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* 错误列表 */}
      <Card
        title="错误日志"
        extra={
          <div className="flex gap-4">
            <Select
              placeholder="严重程度"
              allowClear
              style={{ width: 120 }}
              value={filters.severity}
              onChange={(value) => setFilters((prev) => ({ ...prev, severity: value }))}
              options={[
                { label: 'Low', value: 'low' },
                { label: 'Medium', value: 'medium' },
                { label: 'High', value: 'high' },
                { label: 'Critical', value: 'critical' },
              ]}
            />
            <Select
              placeholder="错误类型"
              allowClear
              style={{ width: 140 }}
              value={filters.type}
              onChange={(value) => setFilters((prev) => ({ ...prev, type: value }))}
              options={[
                { label: '安全攻击', value: 'security_attack' },
                { label: '服务器错误', value: 'server_error' },
                { label: '客户端错误', value: 'client_error' },
              ]}
            />
            <div className="flex items-center gap-2">
              <span>仅显示未解决:</span>
              <Switch
                checked={filters.resolved === false}
                onChange={(checked) => setFilters((prev) => ({ ...prev, resolved: checked ? false : undefined }))}
              />
            </div>
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={errors}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: fetchErrors,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  )
}
